import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast';
import { useCurrent, useDelete, useFetch, usePut } from '../hooks';
import TicketInfo from '../components/tickets/TicketInfo';
import TicketDetailsList from '../components/tickets/TicketDetailsList';
import CreateTicketDetails from '../components/tickets/CreateTicketDetails';
import { getLocalizedOptions, getPriorityOptions, normalizeSupportSettings } from '../constants/ticket';
import { AlertMsg, ConfirmDelete, MwButton, MwInputText, MwModal, MwMultiSelector, MwSelector, MwSpinnerButton, MwTextArea } from '../components/ui';
import { buildAssigneesPayload, toAssigneeOptions } from '../utils/ticketAssignees';
import ProjectInfoCard from '../components/projects/ProjectInfoCard';
import { projectOptionLabel, sprintOptionLabel } from '../utils/projectHelpers';
import LangSwitch, { withLangLabel } from '../components/ui/LangSwitch';
import { arabicValueForEdit, validateBilingual } from '../utils/localized';

const stripHtmlText = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/ /g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const toDatetimeLocalValue = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIsoFromDatetimeLocal = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const [datePart, timePart] = raw.split('T');
  if (!datePart || !timePart) return null;
  const [y, m, d] = datePart.split('-').map((part) => Number(part));
  const [hh, mm] = timePart.split(':').map((part) => Number(part));
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
  const date = new Date(y, m - 1, d, hh, mm, 0, 0);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

const readJwtPayload = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const readPersistedUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

const resolveAuthFlag = (user, key) => {
  const persistedUser = readPersistedUser();
  const tokenPayload = readJwtPayload();
  return isTruthyFlag(
    user?.[key] ??
    user?.user?.[key] ??
    persistedUser?.[key] ??
    persistedUser?.user?.[key] ??
    tokenPayload?.[key] ??
    tokenPayload?.user?.[key]
  );
};

const TicketEditModalContent = ({
  onHide,
  currentLangId,
  supportSettings,
  tenantOptions,
  tenantSelected,
  setTenantSelected,
  projectOptions,
  projectSelected,
  setProjectSelected,
  sprintOptions = [],
  sprintSelected,
  setSprintSelected,
  visibilityOptions,
  visibilitySelected,
  setVisibilitySelected,
  createdAt,
  setCreatedAt,
  expectedAt,
  setExpectedAt,
  adminOptions,
  assigneesSelected,
  setAssigneesSelected,
  projectManagerSelected,
  setProjectManagerSelected,
  canManageProjectManager,
  subject,
  setSubject,
  content,
  setContent,
  subjectEn,
  setSubjectEn,
  contentEn,
  setContentEn,
  sectionSelected,
  setSectionSelected,
  productSelected,
  setProductSelected,
  prioritySelected,
  setPrioritySelected,
  loading,
  error,
  onSave
}) => {
  const sectionOptions = getLocalizedOptions(supportSettings?.sections, currentLangId);
  const productOptions = getLocalizedOptions(supportSettings?.products, currentLangId);
  const priorityOptions = getPriorityOptions(currentLangId);
  const isAr = currentLangId !== 'en';
  const [formLang, setFormLang] = useState(isAr ? 'ar' : 'en');
  const [bilingualCheck, setBilingualCheck] = useState(null);
  const runBilingualCheck = () => validateBilingual([
    { key: 'subject', label: isAr ? 'العنوان' : 'Subject', required: true, ar: subject, en: subjectEn },
    { key: 'content', label: isAr ? 'محتوى التذكرة' : 'Ticket content', required: true, ar: content, en: contentEn },
  ], { isAr, preferredLang: formLang });
  // بعد أول محاولة حفظ: الأخطاء بتتحدث مع الكتابة.
  const liveCheck = bilingualCheck ? runBilingualCheck() : null;
  // زرار الحفظ مش بيتقفل بسبب العنوان / المحتوى — بيتحقق ويقول فين المشكلة.
  const saveWithValidation = () => {
    const result = runBilingualCheck();
    setBilingualCheck(result);
    if (!result.valid) {
      setFormLang(result.firstLang);
      return;
    }
    onSave?.();
  };

  return (
    <div className="p-4 md:p-5 flex flex-col gap-4">
      {error && (
        <AlertMsg>
          {String(error)}
        </AlertMsg>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <MwSelector
          className="w-full z-[80]"
          label={currentLangId === 'ar' ? 'العميل' : 'Customer'}
          initalValue={currentLangId === 'ar' ? 'اختر عميل' : 'Select customer'}
          dataType="labelValue"
          _data={tenantOptions}
          selectedItem={tenantSelected}
          setSelectedItem={setTenantSelected}
          withAddNew={false}
        />
        <MwSelector
          className="w-full z-[75]"
          label={currentLangId === 'ar' ? 'المشروع (اختياري)' : 'Project (optional)'}
          initalValue={currentLangId === 'ar' ? 'بدون مشروع' : 'No project'}
          dataType="labelValue"
          _data={projectOptions}
          selectedItem={projectSelected || projectOptions[0]}
          setSelectedItem={setProjectSelected}
          withAddNew={false}
        />
        {/* الدورة بتظهر بس لو المشروع المختار له دورات. */}
        {projectSelected?.value && sprintOptions.length > 1 && (
          <MwSelector
            className="w-full z-[72]"
            label={currentLangId === 'ar' ? 'الدورة (Sprint)' : 'Sprint'}
            initalValue={currentLangId === 'ar' ? 'بدون دورة' : 'No sprint'}
            dataType="labelValue"
            _data={sprintOptions}
            selectedItem={sprintSelected || sprintOptions[0]}
            setSelectedItem={setSprintSelected}
            withAddNew={false}
          />
        )}
        <MwSelector
          className="w-full z-[70]"
          label={currentLangId === 'ar' ? 'نوع التذكرة' : 'Ticket Type'}
          initalValue={currentLangId === 'ar' ? 'اختر النوع' : 'Select type'}
          dataType="labelValue"
          _data={visibilityOptions}
          selectedItem={visibilitySelected}
          setSelectedItem={setVisibilitySelected}
          withAddNew={false}
        />
        <MwInputText
          label={currentLangId === 'ar' ? 'تاريخ التذكرة' : 'Ticket Date'}
          inputType="datetime-local"
          value={createdAt}
          onChange={(e) => setCreatedAt(e.target.value)}
        />
        <MwInputText
          label={currentLangId === 'ar' ? 'الوقت المتوقع' : 'Expected time'}
          inputType="datetime-local"
          value={expectedAt}
          onChange={(e) => setExpectedAt(e.target.value)}
        />
        <div className="lg:col-span-2">
          <MwMultiSelector
            className="w-full z-[60]"
            label={currentLangId === 'ar' ? 'تعيين التذكرة لموظفي الدعم' : 'Assign Ticket to Support Admins'}
            initalValue={currentLangId === 'ar' ? 'غير معينة — اختر موظف أو أكثر...' : 'Unassigned — select one or more admins...'}
            _data={adminOptions}
            selectedItems={assigneesSelected}
            setSelectedItems={setAssigneesSelected}
          />
        </div>
        {canManageProjectManager && (
          <div className="lg:col-span-2">
            <MwSelector
              className="w-full z-[55]"
              label={currentLangId === 'ar' ? 'مدير المشروع' : 'Project Manager'}
              initalValue={currentLangId === 'ar' ? 'اختر مدير المشروع...' : 'Select project manager...'}
              dataType="labelValue"
              _data={adminOptions}
              selectedItem={projectManagerSelected}
              setSelectedItem={setProjectManagerSelected}
              withAddNew={false}
              showAvatarFallback
            />
          </div>
        )}
        <div className="lg:col-span-2 flex flex-col gap-1">
          <LangSwitch
            value={formLang}
            onChange={setFormLang}
            isAr={isAr}
            filled={{ ar: Boolean(subject?.trim() || stripHtmlText(content)), en: Boolean(subjectEn?.trim() || stripHtmlText(contentEn)) }}
            errors={liveCheck?.errors}
            messages={liveCheck?.messages}
          />
          <MwInputText
            label={withLangLabel(isAr ? 'العنوان' : 'Subject', formLang, isAr)}
            value={formLang === 'ar' ? subject : subjectEn}
            onChange={(e) => (formLang === 'ar' ? setSubject : setSubjectEn)(e.target.value)}
            invalid={!!liveCheck?.errors?.[formLang]?.subject}
            invalidMsg={liveCheck?.errors?.[formLang]?.subject}
          />
        </div>
        <MwSelector
          className="w-full z-50"
          label={currentLangId === 'ar' ? 'القسم' : 'Section'}
          initalValue={currentLangId === 'ar' ? 'اختر القسم' : 'Select section'}
          dataType="labelValue"
          _data={sectionOptions}
          selectedItem={sectionSelected}
          setSelectedItem={setSectionSelected}
          withAddNew={false}
        />
        <MwSelector
          className="w-full z-40"
          label={currentLangId === 'ar' ? 'المنتج' : 'Product'}
          initalValue={currentLangId === 'ar' ? 'اختر المنتج' : 'Select product'}
          dataType="labelValue"
          _data={productOptions}
          selectedItem={productSelected}
          setSelectedItem={setProductSelected}
          withAddNew={false}
        />
        <MwSelector
          className="w-full z-30"
          label={currentLangId === 'ar' ? 'الأولوية' : 'Priority'}
          initalValue={currentLangId === 'ar' ? 'اختر الأولوية' : 'Select priority'}
          dataType="labelValue"
          _data={priorityOptions}
          selectedItem={prioritySelected}
          setSelectedItem={setPrioritySelected}
          withAddNew={false}
        />
      </div>
      {['ar', 'en'].map((code) => (
        <div key={code} className={formLang === code ? '' : 'hidden'}>
          <MwTextArea
            label={withLangLabel(isAr ? 'محتوى التذكرة' : 'Ticket Content', code, isAr)}
            minHeight={480}
            value={code === 'ar' ? content : contentEn}
            onChange={(e) => (code === 'ar' ? setContent : setContentEn)(e.target.value)}
            invalid={!!liveCheck?.errors?.[code]?.content}
            invalidMsg={liveCheck?.errors?.[code]?.content}
            dir={code === 'ar' ? 'rtl' : 'ltr'}
            rich
          />
        </div>
      ))}
      <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 sticky bottom-0 bg-slate-50">
        <MwButton type="secondary" onClick={onHide}>
          {currentLangId === 'ar' ? 'إلغاء' : 'Cancel'}
        </MwButton>
        {loading ? (
          <MwSpinnerButton />
        ) : (
          <MwButton
            type="saveBtn"
            onClick={saveWithValidation}
            disabled={!tenantSelected?.value || !visibilitySelected?.value || !createdAt || !expectedAt || !sectionSelected?.value || !productSelected?.value || !prioritySelected?.value}
          >
            {currentLangId === 'ar' ? 'حفظ التعديل' : 'Save'}
          </MwButton>
        )}
      </div>
    </div>
  );
};

const TicketsShow = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = params.id;
  const tenant = params.tenant;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { user, currentLangId } = useCurrent();
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editSubjectEn, setEditSubjectEn] = useState('');
  const [editContentEn, setEditContentEn] = useState('');
  const [editTenantSelected, setEditTenantSelected] = useState(null);
  const [editProjectSelected, setEditProjectSelected] = useState(null);
  const [editSprintSelected, setEditSprintSelected] = useState(null);
  const [editVisibilitySelected, setEditVisibilitySelected] = useState(null);
  const [editCreatedAt, setEditCreatedAt] = useState('');
  const [editExpectedAt, setEditExpectedAt] = useState('');
  const [editAssigneesSelected, setEditAssigneesSelected] = useState([]);
  const [editProjectManagerSelected, setEditProjectManagerSelected] = useState(null);
  const [editSectionSelected, setEditSectionSelected] = useState(null);
  const [editProductSelected, setEditProductSelected] = useState(null);
  const [editPrioritySelected, setEditPrioritySelected] = useState(null);
  const [editError, setEditError] = useState(null);
  const { putData, loading: editLoading } = usePut();
  const { data: ticketResult, loading: ticketLoading, error: ticketError, refreshHandler } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/${id}`);
  const { data: supportSettingsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`);
  const { data: statusesResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/statuses/admin`);
  const { data: tenantsResult } = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/tenants/getAll`);
  const { data: adminsResult } = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/users/support-admins`);
  const { data: projectsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin/options`);
  const { deleteItem, data: deleteResult, loading: deleteLoading } = useDelete();
  
  const ticket = ticketResult?.data;
  const supportSettingsBase = useMemo(() => normalizeSupportSettings(supportSettingsResult), [supportSettingsResult]);
  const supportSettings = useMemo(() => ({
    ...supportSettingsBase,
    statuses: normalizeSupportSettings({ data: { statuses: statusesResult?.data || [] } }).statuses
  }), [supportSettingsBase, statusesResult]);

  const callBackOnCreated = useCallback(() => {
    refreshHandler()
  }, [refreshHandler])

  const isSuperAdmin = useMemo(() => {
    const persistedRole = (() => {
      try {
        const persistedUser = JSON.parse(localStorage.getItem('user') || 'null');
        return (
          persistedUser?.role ||
          persistedUser?.user?.role ||
          null
        );
      } catch {
        return null;
      }
    })();

    const tokenRole = (() => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length < 2) return null;
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
        const json = atob(padded);
        const data = JSON.parse(json);
        return data?.role || data?.user?.role || null;
      } catch {
        return null;
      }
    })();

    const role =
      user?.role ||
      user?.user?.role ||
      persistedRole ||
      tokenRole ||
      null;

    return role === 'admin';
  }, [user?.role, user?.user?.role]);

  const canEditTicket = useMemo(
    () =>
      isSuperAdmin ||
      (resolveAuthFlag(user, 'supportAdminEnabled') &&
        (
          resolveAuthFlag(user, 'supportCanEditTickets') ||
          (resolveAuthFlag(user, 'supportSuperAdminEnabled') && resolveAuthFlag(user, 'supportCanEditAnyTicket'))
        )),
    [isSuperAdmin, user]
  );

  const currentUserId = useMemo(() => {
    const persistedUserId = (() => {
      try {
        const persistedUser = JSON.parse(localStorage.getItem('user') || 'null');
        return (
          persistedUser?._id ||
          persistedUser?.id ||
          persistedUser?.userId ||
          persistedUser?.user?._id ||
          persistedUser?.user?.id ||
          persistedUser?.user?.userId ||
          null
        );
      } catch {
        return null;
      }
    })();

    const tokenUserId = (() => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length < 2) return null;
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
        const json = atob(padded);
        const data = JSON.parse(json);
        return data?._id || data?.id || data?.userId || data?.sub || null;
      } catch {
        return null;
      }
    })();

    return (
      user?._id ||
      user?.id ||
      user?.userId ||
      user?.user?._id ||
      user?.user?.id ||
      user?.user?.userId ||
      persistedUserId ||
      tokenUserId ||
      null
    );
  }, [user]);

  const isProjectManagerForTicket = useMemo(() => (
    Boolean(
      currentUserId &&
      ticket?.projectManagerId &&
      String(ticket.projectManagerId) === String(currentUserId)
    )
  ), [currentUserId, ticket?.projectManagerId]);

  const effectiveCanEditTicket = canEditTicket || isProjectManagerForTicket;
  const canManageProjectManager = isSuperAdmin || (
    resolveAuthFlag(user, 'supportAdminEnabled') &&
    resolveAuthFlag(user, 'supportCanAssignProjectManager')
  );

  const canDeleteTicket = useMemo(() => {
    if (isSuperAdmin) return true;
    if (
      resolveAuthFlag(user, 'supportAdminEnabled') &&
      resolveAuthFlag(user, 'supportSuperAdminEnabled') &&
      resolveAuthFlag(user, 'supportCanDeleteAnyTicket')
    ) return true;
    // حذف التذكرة للسوبر أدمن فقط (مش لمنشئ التذكرة أو مدير المشروع).
    return false;
  }, [isSuperAdmin, user]);

  const editSectionOptions = useMemo(
    () => getLocalizedOptions(supportSettings?.sections, currentLangId),
    [currentLangId, supportSettings?.sections]
  );
  const editProductOptions = useMemo(
    () => getLocalizedOptions(supportSettings?.products, currentLangId),
    [currentLangId, supportSettings?.products]
  );
  const editPriorityOptions = useMemo(() => getPriorityOptions(currentLangId), [currentLangId]);
  const tenants = useMemo(() => (Array.isArray(tenantsResult?.data) ? tenantsResult.data : []), [tenantsResult]);
  const getTenantLabel = useCallback((tenantItem) => tenantItem?.tenantName || tenantItem?.username || tenantItem?._id || '', []);
  const tenantOptions = useMemo(() => tenants.map((tenantItem) => ({
    label: getTenantLabel(tenantItem),
    value: tenantItem?._id,
    lang: currentLangId
  })), [currentLangId, getTenantLabel, tenants]);
  const projects = useMemo(() => (Array.isArray(projectsResult?.data) ? projectsResult.data : []), [projectsResult]);
  const editProjectOptions = useMemo(() => {
    const options = projects
      .filter((project) => !editTenantSelected?.value || String(project.tenantId) === String(editTenantSelected.value))
      .map((project) => ({ label: projectOptionLabel(project), value: project._id, lang: currentLangId }));
    // المشروع الحالي ممكن ميكونش في القائمة (مثلاً خارج نطاق المستخدم) — نعرضه برضه.
    if (ticket?.projectId && !options.some((opt) => String(opt.value) === String(ticket.projectId))) {
      options.unshift({ label: projectOptionLabel({ projectNumber: ticket.projectNumber, title: ticket.projectTitle }), value: ticket.projectId, lang: currentLangId });
    }
    return [{ label: currentLangId === 'ar' ? 'بدون مشروع' : 'No project', value: '', lang: currentLangId }, ...options];
  }, [currentLangId, editTenantSelected?.value, projects, ticket?.projectId, ticket?.projectNumber, ticket?.projectTitle]);
  const editSprintsUrl = showEditModal && editProjectSelected?.value
    ? `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin/${editProjectSelected.value}/sprints`
    : null;
  const { data: editSprintsResult } = useFetch(editSprintsUrl, Boolean(editSprintsUrl));
  const editSprintOptions = useMemo(() => {
    const sprints = Array.isArray(editSprintsResult?.data) ? editSprintsResult.data : [];
    const options = sprints.map((sprint) => ({
      label: sprintOptionLabel(sprint, currentLangId),
      value: sprint._id,
      lang: currentLangId
    }));
    return [{ label: currentLangId === 'ar' ? 'بدون دورة' : 'No sprint', value: '', lang: currentLangId }, ...options];
  }, [currentLangId, editSprintsResult]);
  const selectEditProject = useCallback((option) => {
    const project = projects.find((item) => String(item._id) === String(option?.value || ''));
    setEditProjectSelected(option?.value ? option : null);
    // الدورات خاصة بكل مشروع — تغيير المشروع بيشيل الدورة المختارة.
    setEditSprintSelected((current) => (String(option?.value || '') === String(ticket?.projectId || '') && current ? current : null));
    if (project) {
      const projectTenant = tenantOptions.find((opt) => String(opt.value) === String(project.tenantId));
      if (projectTenant) setEditTenantSelected(projectTenant);
    }
  }, [projects, tenantOptions, ticket?.projectId]);
  const selectEditTenant = useCallback((option) => {
    setEditTenantSelected(option);
    setEditProjectSelected((current) => {
      if (!current?.value) return current;
      const project = projects.find((item) => String(item._id) === String(current.value));
      const projectTenantId = project?.tenantId ?? (String(current.value) === String(ticket?.projectId) ? ticket?.tenantId : null);
      return projectTenantId && String(projectTenantId) !== String(option?.value) ? null : current;
    });
  }, [projects, ticket?.projectId, ticket?.tenantId]);
  const visibilityOptions = useMemo(() => ([
    { label: currentLangId === 'ar' ? 'خارجي - مشاركة مع العميل و فريق العمل' : 'External - Shared with customer and team', value: 'external', lang: currentLangId },
    { label: currentLangId === 'ar' ? 'داخلي - داخل فريق العمل فقط' : 'Internal - Team only', value: 'internal', lang: currentLangId }
  ]), [currentLangId]);
  const adminOptions = useMemo(() => {
    const admins = Array.isArray(adminsResult?.data) ? adminsResult.data : [];
    return [
      { label: currentLangId === 'ar' ? 'غير معين' : 'Unassigned', value: '', lang: currentLangId },
      ...admins.map((admin) => ({
        label: admin?.name || admin?.username || admin?.email || admin?._id,
        value: admin?._id,
        lang: currentLangId,
        email: admin?.email || '',
        subLabel: admin?.jobTitle || '',
        avatar: admin?.userPic || admin?.avatar || admin?.image || admin?.profileImage || admin?.photo || ''
      }))
    ];
  }, [adminsResult, currentLangId]);

  const hydrateEditForm = useCallback(() => {
    if (!ticket?._id) return;
    setEditError(null);
    setEditTenantSelected(
      tenantOptions.find((opt) => opt.value === ticket?.tenantId) ||
      (ticket?.tenantId ? { label: ticket?.tenantName || ticket?.tenantId, value: ticket?.tenantId, lang: currentLangId } : null)
    );
    setEditProjectSelected(
      ticket?.projectId
        ? { label: projectOptionLabel({ projectNumber: ticket.projectNumber, title: ticket.projectTitle }), value: ticket.projectId, lang: currentLangId }
        : null
    );
    setEditSprintSelected(
      ticket?.sprintId
        ? { label: sprintOptionLabel({ sprintNumber: ticket.sprintNumber, title: ticket.sprintTitle }, currentLangId), value: ticket.sprintId, lang: currentLangId }
        : null
    );
    setEditVisibilitySelected(visibilityOptions.find((opt) => opt.value === (ticket?.visibility || 'external')) || visibilityOptions[0]);
    setEditCreatedAt(toDatetimeLocalValue(ticket?.createdAt));
    setEditExpectedAt(toDatetimeLocalValue(ticket?.expectedAt));
    setEditAssigneesSelected(toAssigneeOptions(ticket, adminOptions, currentLangId));
    setEditProjectManagerSelected(
      adminOptions.find((opt) => String(opt.value || '') === String(ticket?.projectManagerId || '')) ||
      (ticket?.projectManagerId ? { label: ticket?.projectManagerName || ticket?.projectManagerId, value: ticket?.projectManagerId, lang: currentLangId } : adminOptions[0] || null)
    );
    setEditSubject(arabicValueForEdit(ticket, 'subject'));
    setEditContent(arabicValueForEdit(ticket, 'content'));
    setEditSubjectEn(ticket?.subjectEn || '');
    setEditContentEn(ticket?.contentEn || '');
    setEditSectionSelected(editSectionOptions.find((opt) => opt.value === ticket?.sectionId) || null);
    setEditProductSelected(editProductOptions.find((opt) => opt.value === ticket?.ProductId) || null);
    setEditPrioritySelected(editPriorityOptions.find((opt) => opt.value === (ticket?.priority || 'medium')) || editPriorityOptions.find((opt) => opt.value === 'medium') || null);
  }, [adminOptions, currentLangId, editPriorityOptions, editProductOptions, editSectionOptions, tenantOptions, ticket, visibilityOptions]);

  const openEditModal = useCallback(() => {
    hydrateEditForm();
    setShowEditModal(true);
  }, [hydrateEditForm]);

  useEffect(() => {
    if (!ticket?._id) return;
    if (!effectiveCanEditTicket) return;
    if (searchParams.get('edit') !== '1') return;
    openEditModal();
    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
  }, [effectiveCanEditTicket, openEditModal, searchParams, setSearchParams, ticket?._id]);

  const editSaveHandler = useCallback(async () => {
    if (!id) return;
    if (!effectiveCanEditTicket) return;
    setEditError(null);
    try {
      const selectedTenantRecord = tenants.find((tenantItem) => String(tenantItem?._id) === String(editTenantSelected?.value));
      const selectedCompany = selectedTenantRecord?.companies?.[0];
      const selectedBranch = selectedCompany?.branches?.[0];
      const selectedTenantOwner = selectedTenantRecord?.ownerUser || null;
      const selectedProjectManager = editProjectManagerSelected?.value
        ? adminOptions.find((opt) => String(opt?.value || '') === String(editProjectManagerSelected.value)) || editProjectManagerSelected
        : null;
      await putData(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/${id}`, {
        tenantId: editTenantSelected?.value || null,
        tenantName: selectedTenantRecord ? getTenantLabel(selectedTenantRecord) : ticket?.tenantName || '',
        customerName: selectedTenantRecord
          ? (selectedTenantOwner?.name || selectedTenantRecord?.contactPersonName || getTenantLabel(selectedTenantRecord) || '')
          : ticket?.customerName,
        customerId: selectedTenantRecord
          ? (selectedTenantOwner?._id || selectedTenantRecord?.userId || selectedTenantRecord?._id || '')
          : ticket?.customerId,
        email: selectedTenantRecord
          ? (selectedTenantOwner?.email || selectedCompany?.mainEmail || ticket?.email || '')
          : ticket?.email,
        mobile: selectedTenantRecord
          ? (selectedTenantOwner?.mobile || selectedTenantRecord?.contactPersonMobile || selectedTenantRecord?.tenantPhone || selectedCompany?.mainPhone || '')
          : ticket?.mobile,
        companyId: selectedTenantRecord ? selectedCompany?._id || null : ticket?.companyId,
        branchId: selectedTenantRecord ? selectedBranch?._id || null : ticket?.branchId,
        ...(String(editProjectSelected?.value || '') !== String(ticket?.projectId || '')
          ? { projectId: editProjectSelected?.value || null }
          : {}),
        ...(String(editSprintSelected?.value || '') !== String(ticket?.sprintId || '')
          ? { sprintId: editProjectSelected?.value ? editSprintSelected?.value || null : null }
          : {}),
        visibility: editVisibilitySelected?.value || 'external',
        createdAt: toIsoFromDatetimeLocal(editCreatedAt),
        expectedAt: toIsoFromDatetimeLocal(editExpectedAt),
        ...buildAssigneesPayload(editAssigneesSelected),
        ...(canManageProjectManager ? {
          projectManagerId: selectedProjectManager?.value || null,
          projectManagerName: selectedProjectManager?.value ? selectedProjectManager?.label || '' : '',
          projectManagerEmail: selectedProjectManager?.value ? selectedProjectManager?.email || '' : ''
        } : {}),
        subject: editSubject,
        content: editContent,
        subjectEn: editSubjectEn,
        contentEn: editContentEn,
        sectionId: editSectionSelected?.value || null,
        ProductId: editProductSelected?.value || null,
        priority: editPrioritySelected?.value || 'medium'
      });
      setShowEditModal(false);
      toast.success(currentLangId === 'ar' ? 'تم تعديل التذكرة بنجاح' : 'Ticket updated successfully');
      refreshHandler();
    } catch (err) {
      setEditError(err?.response?.data?.message || (currentLangId === 'ar' ? 'فشل تعديل التذكرة' : 'Failed to update ticket'));
    }
  }, [adminOptions, canManageProjectManager, editProjectManagerSelected, editProjectSelected?.value, editSprintSelected?.value, effectiveCanEditTicket, currentLangId, editAssigneesSelected, editContent, editContentEn, editSubjectEn, editCreatedAt, editExpectedAt, editPrioritySelected?.value, editProductSelected?.value, editSectionSelected?.value, editSubject, editTenantSelected?.value, editVisibilitySelected?.value, getTenantLabel, id, putData, refreshHandler, tenants, ticket]);

  const deleteHandler = useCallback(() => {
    if (!id) return;
    if (!canDeleteTicket) return;
    deleteItem(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/${id}`);
  }, [canDeleteTicket, deleteItem, id]);

  useEffect(() => {
    if (deleteResult?.status === 1) {
      if (typeof window !== 'undefined' && id) {
        window.dispatchEvent(new CustomEvent('support-ticket-deleted', { detail: { id: String(id) } }));
      }
      navigate(`/${tenant}/tickets`);
    }
  }, [deleteResult, id, navigate, tenant]);

  useEffect(() => {
    if (!canDeleteTicket && showDeleteConfirm) {
      setShowDeleteConfirm(false);
    }
  }, [canDeleteTicket, showDeleteConfirm]);

  return (
    <div className="admin-page">
      <div className="admin-panel p-3 md:p-4">
        <div className="flex items-center justify-end gap-2 mb-3 flex-wrap">
          {effectiveCanEditTicket && (
            <MwButton type="secondary" onClick={openEditModal}>
              {currentLangId === 'ar' ? 'تعديل التذكرة' : 'Edit Ticket'}
            </MwButton>
          )}
          {canDeleteTicket && (
            <MwButton type='deleteBtn' onClick={() => setShowDeleteConfirm(true)}>
              {currentLangId === 'ar' ? 'حذف التذكرة' : 'Delete Ticket'}
            </MwButton>
          )}
          <MwButton type="cancelBtn" withIcon={false} onClick={() => navigate(`/${tenant}/tickets`)}>
            {currentLangId === 'ar' ? 'إدارة التذاكر' : 'Tickets Management'}
          </MwButton>
        </div>
        {ticket?.project && (
          <div className="mb-4">
            <ProjectInfoCard
              project={ticket.project}
              supportSettings={supportSettings}
              lang={currentLangId}
              admins={Array.isArray(adminsResult?.data) ? adminsResult.data : []}
              showLink
              showDescription={false}
            />
          </div>
        )}
        <TicketInfo loading={ticketLoading} error={ticketError} item={ticket} supportSettings={supportSettings} onStatusUpdated={refreshHandler} />
      </div>
      {ticket && (
        <div className="admin-panel p-3 md:p-4">
          <CreateTicketDetails ticketID={ticket?._id} supportSettings={supportSettings} currentStatusValue={ticket?.statusId || ticket?.status} statusLockedBySuperAdmin={ticket?.statusLockedBySuperAdmin === true} callBackOnCreated={callBackOnCreated} />
        </div>
      )}
      {ticket && (
        <div className="admin-panel p-3 md:p-4">
          <TicketDetailsList loading={ticketLoading} data={ticket?.details} ticket={ticket} supportSettings={supportSettings} onRefresh={refreshHandler} />
        </div>
      )}
      {effectiveCanEditTicket && (
        <MwModal
          title={currentLangId === 'ar' ? 'تعديل التذكرة' : 'Edit Ticket'}
          onShow={showEditModal}
          setOnShow={setShowEditModal}
          panelClassName="max-w-3xl max-h-[min(760px,calc(100vh-32px))]"
          bodyClassName="max-h-[calc(100vh-92px)]"
        >
          <TicketEditModalContent
            currentLangId={currentLangId}
            supportSettings={supportSettings}
            tenantOptions={tenantOptions}
            tenantSelected={editTenantSelected}
            setTenantSelected={selectEditTenant}
            projectOptions={editProjectOptions}
            projectSelected={editProjectSelected}
            setProjectSelected={selectEditProject}
            sprintOptions={editSprintOptions}
            sprintSelected={editSprintSelected}
            setSprintSelected={setEditSprintSelected}
            visibilityOptions={visibilityOptions}
            visibilitySelected={editVisibilitySelected}
            setVisibilitySelected={setEditVisibilitySelected}
            createdAt={editCreatedAt}
            setCreatedAt={setEditCreatedAt}
            expectedAt={editExpectedAt}
            setExpectedAt={setEditExpectedAt}
            adminOptions={adminOptions}
            assigneesSelected={editAssigneesSelected}
            setAssigneesSelected={setEditAssigneesSelected}
            projectManagerSelected={editProjectManagerSelected}
            setProjectManagerSelected={setEditProjectManagerSelected}
            canManageProjectManager={canManageProjectManager}
            subject={editSubject}
            setSubject={setEditSubject}
            content={editContent}
            setContent={setEditContent}
            subjectEn={editSubjectEn}
            setSubjectEn={setEditSubjectEn}
            contentEn={editContentEn}
            setContentEn={setEditContentEn}
            sectionSelected={editSectionSelected}
            setSectionSelected={setEditSectionSelected}
            productSelected={editProductSelected}
            setProductSelected={setEditProductSelected}
            prioritySelected={editPrioritySelected}
            setPrioritySelected={setEditPrioritySelected}
            loading={editLoading}
            error={editError}
            onSave={editSaveHandler}
          />
        </MwModal>
      )}
      {canDeleteTicket && (
        <ConfirmDelete
          title={currentLangId === 'ar' ? 'حذف التذكرة' : 'Delete Ticket'}
          msg={currentLangId === 'ar'
            ? 'سيتم حذف التذكرة بالكامل مع كل الردود المضافة عليها، ولا يمكن التراجع عن هذا الإجراء.'
            : 'This ticket and all of its replies will be deleted. This action cannot be undone.'}
          onShow={showDeleteConfirm}
          setOnShow={setShowDeleteConfirm}
          loading={deleteLoading}
          onDeleteHandler={deleteHandler}
          confirmLabel={currentLangId === 'ar' ? 'حذف التذكرة' : 'Delete Ticket'}
        />
      )}
    </div>
  )
}
export default TicketsShow

